import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type ReactNode,
} from 'react'
import { cn } from './cn'

export type TooltipProps = {
  /** The element that, when hovered/focused/tapped, shows the tooltip. */
  children: ReactNode
  /** Tooltip body. Plain text reads cleanest; `<>JSX</>` allowed. */
  content: ReactNode
  /** Side relative to the trigger. Default `top`. */
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Open delay in ms before the tooltip appears on hover. Default 200. */
  delayDuration?: number
  /** Disable the tooltip without unmounting the trigger. */
  disabled?: boolean
  /** Extra className for the content surface. */
  className?: string
}

type TriggerProps = {
  ref?: React.Ref<HTMLElement>
  onPointerDown?: (e: ReactPointerEvent<HTMLElement>) => void
}

/**
 * App-styled tooltip — Radix-backed, theme-tokenised.
 *
 * Behaviour:
 * - Mouse / pen: hover or keyboard-focus opens it (Radix default).
 * - Touch: a tap toggles it open; tapping outside or pressing Escape closes.
 *   This way icon-only buttons stay readable on iPad / phones where hover
 *   never fires.
 *
 * Use for short labels on icon-only buttons, data points, status dots, or
 * abbreviated values (e.g. "€12,840" with "Ingresos cobrados últimos 30
 * días" on hover). For inline help next to a label use `<InfoTooltip>`
 * instead — that one is opt-in via a "?" trigger.
 *
 * Accessibility: the trigger element MUST be a focusable native element
 * (`<button>`, `<a>`, `<input>`). Wrapping a `<div>` requires `tabIndex={0}`
 * to participate in keyboard nav. Radix handles aria-describedby + focus
 * management automatically.
 *
 * @example
 *   <Tooltip content="Quitar fila">
 *     <button aria-label="Eliminar"><Trash size={14} /></button>
 *   </Tooltip>
 */
export function Tooltip({
  children,
  content,
  side = 'top',
  delayDuration = 200,
  disabled = false,
  className,
}: TooltipProps) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDocPointerDown = (e: globalThis.PointerEvent) => {
      const t = e.target as Node | null
      if (triggerRef.current?.contains(t ?? null)) return
      if (contentRef.current?.contains(t ?? null)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onDocPointerDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (disabled || !isValidElement(children)) {
    return <>{children}</>
  }

  const child = children as ReactElement<TriggerProps>
  const triggerWithTap = cloneElement(child, {
    ref: triggerRef,
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      child.props.onPointerDown?.(e)
      if (e.pointerType === 'touch') {
        setOpen((prev) => !prev)
      }
    },
  })

  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen}>
        <TooltipPrimitive.Trigger asChild>{triggerWithTap}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            ref={contentRef}
            side={side}
            sideOffset={6}
            className={cn('tooltip-content', className)}
          >
            {content}
            <TooltipPrimitive.Arrow className="tooltip-arrow" width={10} height={5} />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}
